// The Striping Plan tab's side panel (spec 2026-10-14 Part B): the route being edited,
// how its lanes pair into roads, and its intersection list in route order. All changes
// go through the editor (useStripingEditor), which records undo history.
//
// The list is drawn along a "road rail" (a dashed centre line) because it IS a sequence
// along the route; each number sits on the rail like its marker on the map.
import React, { useEffect, useRef, useState } from 'react';
import { formatCoord } from '../InternalComponents/stripingGeometry';
import { MAX_STRIPING_NAME } from '../InternalComponents/stripingList';
import './stripingPanel.css';

function NameInput({ row, onCommit }) {
  const [value, setValue] = useState(row.name ?? '');
  useEffect(() => setValue(row.name ?? ''), [row.name]);
  const commit = () => {
    if ((row.name ?? '') !== value) onCommit(row.id, value);
  };
  return (
    <input
      type="text"
      className="striping-panel-name"
      value={value}
      maxLength={MAX_STRIPING_NAME}
      placeholder={`Intersection ${row.number}`}
      aria-label={`Name of intersection ${row.number}`}
      onChange={(e) => setValue(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') e.currentTarget.blur();
      }}
      onClick={(e) => e.stopPropagation()}
    />
  );
}

function RoadsSection({ roads }) {
  const hasLanes = roads.roads.length > 0 || roads.problems.length > 0;
  return (
    <section className="striping-panel-section">
      <h4 className="striping-panel-heading">Roads</h4>
      {hasLanes ? (
        <ul className="striping-panel-roads">
          {roads.roads.map((r) => (
            <li key={r.label} className="striping-panel-road">
              <i className="fa-solid fa-circle-check striping-panel-road-icon" aria-hidden />
              <span className="striping-panel-road-label">{r.label}</span>
              <span className="striping-panel-road-pair">N + P</span>
            </li>
          ))}
          {roads.problems.map((p) => (
            <li key={p.laneId} className="striping-panel-road is-problem">
              <i className="fa-solid fa-triangle-exclamation striping-panel-road-icon" aria-hidden />
              <span className="striping-panel-road-label">{p.laneName}</span>
              <span className="striping-panel-road-reason">{p.reason}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="striping-panel-hint">This route has no lanes yet. Add them on the Lanes tab.</p>
      )}
      <p className="striping-panel-hint">
        A road is an N lane and a P lane with the same lane label and lane position. The
        striping plan skips any lane without such a partner.
      </p>
    </section>
  );
}

export default function StripingPanelContent({ editor, paths = [] }) {
  const { pathId, rows, roads, importNote, selectedPointId } = editor;
  const listRef = useRef(null);
  // Bring the selected row into view (a map click can select a row far down the list).
  useEffect(() => {
    if (!selectedPointId) return;
    const el = [...(listRef.current?.children || [])].find((li) => li.dataset.pointId === selectedPointId);
    el?.scrollIntoView?.({ block: 'nearest' });
  }, [selectedPointId]);

  return (
    <div className="striping-panel">
      <section className="striping-panel-section">
        <label className="striping-panel-field">
          <span className="striping-panel-heading">Route</span>
          <select
            className="striping-panel-select"
            aria-label="Route"
            value={pathId ?? ''}
            onChange={(e) => editor.setSelectedPathId(e.target.value || null)}
          >
            {paths.length === 0 && <option value="">No routes yet</option>}
            {paths.map((p) => (
              <option key={p.id} value={p.id}>
                {String(p.label ?? '').trim() || 'Unnamed route'}
              </option>
            ))}
          </select>
        </label>
      </section>

      <RoadsSection roads={roads} />

      <section className="striping-panel-section">
        <div className="striping-panel-header">
          <h4 className="striping-panel-heading">Intersections</h4>
          <span className="striping-panel-count">{rows.length}</span>
        </div>
        {importNote && (
          <p className={`striping-panel-note${importNote.kind === 'error' ? ' is-error' : ''}`} role="status">
            <i
              className={`fa-solid ${importNote.kind === 'error' ? 'fa-circle-exclamation' : 'fa-file-import'} striping-panel-note-icon`}
              aria-hidden
            />
            <span>{importNote.text}</span>
          </p>
        )}
        {rows.length === 0 ? (
          <div className="striping-panel-empty">
            <p className="striping-panel-empty-title">Click on the map or import a DOT file</p>
            <p className="striping-panel-hint">
              Turn on Add points in the map toolbar, then click where a road meets this route.
            </p>
          </div>
        ) : (
          <ol className="striping-panel-list" ref={listRef} aria-label="Intersections in route order">
            {rows.map((row) => (
              <li
                key={row.id}
                data-point-id={row.id}
                className={`striping-panel-row${row.id === selectedPointId ? ' is-selected' : ''}${row.offRoad ? ' is-off-road' : ''}`}
                onClick={() => editor.selectPoint(row.id)}
              >
                <span className="striping-panel-num" aria-hidden="true">{row.number}</span>
                <div className="striping-panel-main">
                  <NameInput row={row} onCommit={editor.renamePoint} />
                  <span className="striping-panel-coords">
                    {formatCoord(row.lat)}, {formatCoord(row.lng)}
                  </span>
                  {row.offRoad && (
                    <span className="striping-panel-offroad">
                      not on the road — the striping plan will ignore it
                    </span>
                  )}
                </div>
                <div className="striping-panel-actions">
                  <button
                    type="button"
                    className="striping-panel-icon-btn"
                    aria-label={`Zoom to ${row.displayName}`}
                    title="Zoom to"
                    onClick={(e) => {
                      e.stopPropagation();
                      editor.focusPoint(row);
                    }}
                  >
                    <i className="fa-solid fa-location-crosshairs" aria-hidden />
                  </button>
                  <button
                    type="button"
                    className="striping-panel-icon-btn is-danger"
                    aria-label={`Delete ${row.displayName}`}
                    title="Delete"
                    onClick={(e) => {
                      e.stopPropagation();
                      editor.deletePoint(row.id);
                    }}
                  >
                    <i className="fa-solid fa-trash-can" aria-hidden />
                  </button>
                </div>
              </li>
            ))}
          </ol>
        )}
      </section>
    </div>
  );
}
